import React, { useState } from 'react';
import { Database, Activity, Users, Globe, Shield, Zap, ChevronRight, CheckCircle } from 'lucide-react';

const LandingPage = () => {
  const [email, setEmail] = useState('');
  
  const handleSignup = (e) => {
    e.preventDefault();
    // In a real application, this would submit to the backend
    alert(`Sign up request for: ${email}`);
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Database className="h-8 w-8 text-indigo-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">VisitorStatus</span>
            </div>
            <div className="flex items-center space-x-4">
              <a href="#" className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm font-medium">Features</a>
              <a href="#" className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm font-medium">Pricing</a>
              <a href="#" className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm font-medium">Documentation</a>
              <a href="/login" className="ml-4 text-indigo-600 hover:text-indigo-500 px-3 py-2 text-sm font-medium">Login</a>
              <a href="/register" className="ml-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium">Sign up</a>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Hero section */}
      <header className="bg-indigo-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between">
          <div className="lg:w-1/2 mb-12 lg:mb-0">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Know your visitors.<br />
              <span className="text-indigo-600">Grow your business.</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-lg">
              VisitorStatus provides simple, powerful website analytics that respect your visitors' privacy. Understand who's visiting your site and optimize your conversion rates.
            </p>
            <form onSubmit={handleSignup} className="flex flex-col sm:flex-row max-w-md gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="px-4 py-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button
                type="submit"
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md whitespace-nowrap"
              >
                Start for free
              </button>
            </form>
            <p className="text-sm text-gray-500 mt-3">
              No credit card required. Free plan available.
            </p>
          </div>
          <div className="lg:w-1/2">
            <div className="bg-white rounded-lg shadow-xl p-2 max-w-lg mx-auto">
              <img 
                src="/api/placeholder/800/500" 
                alt="VisitorStatus Dashboard Preview" 
                className="rounded"
              />
            </div>
          </div>
        </div>
      </header>
      
      {/* Features section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Powerful features, easy to use</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to understand your website traffic and optimize your visitor experience.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 border border-gray-200 rounded-lg">
              <div className="p-2 rounded-md bg-indigo-100 text-indigo-700 inline-block mb-4">
                <Activity className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Real-time Analytics</h3>
              <p className="text-gray-600">
                See who's on your site right now with accurate, real-time visitor tracking and analytics.
              </p>
            </div>
            
            <div className="p-6 border border-gray-200 rounded-lg">
              <div className="p-2 rounded-md bg-green-100 text-green-700 inline-block mb-4">
                <Users className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Visitor Profiles</h3>
              <p className="text-gray-600">
                Build comprehensive visitor profiles with detailed device, location, and behavior data.
              </p>
            </div>
            
            <div className="p-6 border border-gray-200 rounded-lg">
              <div className="p-2 rounded-md bg-blue-100 text-blue-700 inline-block mb-4">
                <Globe className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Global Coverage</h3>
              <p className="text-gray-600">
                Track and analyze visitors from anywhere in the world with accurate geolocation.
              </p>
            </div>
            
            <div className="p-6 border border-gray-200 rounded-lg">
              <div className="p-2 rounded-md bg-purple-100 text-purple-700 inline-block mb-4">
                <Shield className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Privacy Compliant</h3>
              <p className="text-gray-600">
                GDPR, CCPA, and privacy-first analytics that respect your visitors and comply with regulations.
              </p>
            </div>
            
            <div className="p-6 border border-gray-200 rounded-lg">
              <div className="p-2 rounded-md bg-yellow-100 text-yellow-700 inline-block mb-4">
                <Zap className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Custom Tracking</h3>
              <p className="text-gray-600">
                Add custom parameters to track specific user actions, campaigns, or any data you need.
              </p>
            </div>
            
            <div className="p-6 border border-gray-200 rounded-lg">
              <div className="p-2 rounded-md bg-red-100 text-red-700 inline-block mb-4">
                <Database className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Data Export</h3>
              <p className="text-gray-600">
                Export your analytics data in multiple formats for further analysis or reporting.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* How it works section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How it works</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Getting started with VisitorStatus is quick and easy. You'll be tracking visitors in minutes.
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-start gap-8">
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 font-bold text-xl mb-4">1</div>
                              <h3 className="text-xl font-medium text-gray-900 mb-2">Sign up for free</h3>
              <p className="text-gray-600 mb-4">
                Create your account in seconds. No credit card required to get started with our free plan.</p>
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 font-bold text-xl mb-4">2</div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Add the tracking code</h3>
              <p className="text-gray-600 mb-4">
                Copy and paste a single line of code to your website. Customize it with your own parameters.</p>
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 font-bold text-xl mb-4">3</div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Gain insights</h3>
              <p className="text-gray-600 mb-4">
                Watch real-time data flow in and use powerful analytics to understand your visitors.</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Pricing section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple, transparent pricing</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the plan that works best for your needs. All plans include core features.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Free Plan */}
            <div className="border border-gray-200 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Free</h3>
              <p className="text-gray-600 mb-6">Perfect for personal websites and small projects</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">$0</span>
                <span className="text-gray-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Up to 10,000 pageviews/month</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Basic analytics</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">1 website</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">7-day data retention</span>
                </li>
              </ul>
              <button className="w-full py-3 px-4 border border-indigo-600 text-indigo-600 font-medium rounded-md hover:bg-indigo-50">
                Get started
              </button>
            </div>
            
            {/* Pro Plan */}
            <div className="border-2 border-indigo-600 rounded-lg p-8 relative">
              <div className="absolute top-0 right-0 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                POPULAR
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pro</h3>
              <p className="text-gray-600 mb-6">For growing businesses and professional websites</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">$29</span>
                <span className="text-gray-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Up to 100,000 pageviews/month</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Advanced analytics</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">5 websites</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">30-day data retention</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Custom parameters</span>
                </li>
              </ul>
              <button className="w-full py-3 px-4 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700">
                Start free trial
              </button>
            </div>
            
            {/* Business Plan */}
            <div className="border border-gray-200 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Business</h3>
              <p className="text-gray-600 mb-6">For larger businesses with multiple websites</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">$99</span>
                <span className="text-gray-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Unlimited pageviews</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Full analytics suite</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Unlimited websites</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">12-month data retention</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">API access</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Priority support</span>
                </li>
              </ul>
              <button className="w-full py-3 px-4 border border-indigo-600 text-indigo-600 font-medium rounded-md hover:bg-indigo-50">
                Contact sales
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="py-16 md:py-24 bg-indigo-600">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Start tracking your visitors today</h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            Join thousands of website owners who trust VisitorStatus for their analytics needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="#" className="px-8 py-3 bg-white text-indigo-600 font-medium rounded-md hover:bg-indigo-50">
              Get started for free
            </a>
            <a href="#" className="px-8 py-3 bg-indigo-500 text-white font-medium rounded-md hover:bg-indigo-400">
              See a demo
            </a>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-medium mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white">Features</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Pricing</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Testimonials</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-medium mb-4">Resources</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white">Documentation</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">API</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Status</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-medium mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white">About</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Careers</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Contact</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Press</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-medium mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white">Privacy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Terms</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Cookie Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">GDPR</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <Database className="h-6 w-6 text-indigo-500" />
              <span className="ml-2 text-white font-bold">VisitorStatus</span>
            </div>
            <p className="text-gray-400 text-sm">
              © 2025 VisitorStatus. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;